import { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { langFromPath, stripLang, withLang } from "@/i18n";
import type { SceneKey } from "./Stage";

/** Shell-ul deține unica instanță de motor; paginile îi spun ce să monteze. */
export const StageContext = createContext<{
  setScene: (key: SceneKey | null, hero?: boolean) => void;
} | null>(null);

/** Montează scena cerută cât timp pagina e afișată, și o demontează la ieșire.
 *
 *  `hero` cere regia de landing: obiectul se asamblează singur la încărcare și
 *  se stinge când hero-ul iese din ecran. Fără el, asamblarea urmează scroll-ul
 *  paginii, ceea ce e corect pe un caz și greșit pe landing. */
export function useSceneMount(key: SceneKey | null, hero = false) {
  const ctx = useContext(StageContext);
  useEffect(() => {
    ctx?.setScene(key, hero);
    return () => ctx?.setScene(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, hero]);
}

/** Tipurile pe care le acceptă /api/brief. Ținute aici fiindcă și întrebările
 *  din secțiunea „Where does it hurt?" trimit către ele. */
export type BriefKind = "Web app" | "Mobile app" | "Desktop" | "Automation" | "Not sure yet";

/** Evenimentul prin care o întrebare spune formularului ce tip s-a ales deja.
 *  Un eveniment, nu context sau stare ridicată în părinte: formularul e montat
 *  în altă secțiune, iar singurul lucru care circulă e un șir dintr-o listă
 *  închisă. Context pentru atât ar fi legat două componente degeaba. */
export const BRIEF_PRESET = "brief:preset";

/** Butonul „Start a project" din orice pagină duce la formularul de pe
 *  landing. Pe landing derulează direct; de pe o pagină de caz navighează
 *  întâi acasă, apoi derulează după ce s-a randat.
 *
 *  Cu `kind`, formularul sare peste prima întrebare: cine a apăsat pe
 *  „My team retypes the same data all day" a răspuns deja la „ce construim".
 *
 *  Landing-ul se recunoaște după adresa fără prefix, iar întoarcerea acasă se
 *  face în limba paginii. Varianta dinainte compara cu „/" și naviga la „/":
 *  pe /ro și /ru nicio adresă nu e „/", deci fiecare buton de pe site arunca
 *  vizitatorul pe landing-ul englezesc — adică drumul de conversie era rupt în
 *  două limbi din trei. Adresa vine din router, nu din window.location: la
 *  navigarea în SPA window.location e actualizat, dar a citi starea rutei din
 *  afara router-ului înseamnă să nu reacționezi la ea. */
export function useBrief(kind?: BriefKind) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (e: React.MouseEvent) => {
    e.preventDefault();
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const go = () => {
      document
        .querySelector("#brief")
        ?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
      if (kind) window.dispatchEvent(new CustomEvent(BRIEF_PRESET, { detail: kind }));
    };
    if (stripLang(pathname) === "/") {
      go();
    } else {
      navigate(withLang(langFromPath(pathname), "/"));
      window.setTimeout(go, 80);
    }
  };
}
